import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import DecodeMiniCard from "@/components/DecodeMiniCard";
import DecodeFullReport from "@/components/DecodeFullReport";
import ScoreBars from "@/components/ScoreBars";
import ShareCardComposer from "@/components/ShareCardComposer";
import UseThisWord from "@/components/UseThisWord";
import DecodeFeedback from "@/components/DecodeFeedback";
import { Switch } from "@/components/ui/switch";
import { exportReportPdf } from "@/lib/pdf";
import { Loader2, Sparkles, Lock, BookmarkPlus, ShieldAlert, Globe, FileDown } from "lucide-react";
import { toast } from "sonner";

const TYPES = [
  "Personal Identity", "Artist Name", "Brand Name", "Business Naming", "Song Title",
  "Pop Culture", "Luxury Object", "Relationship Word", "Shadow Word", "Wealth Word", "Status Word",
];

export default function Decode() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, refresh } = useAuth();
  const [text, setText] = useState("");
  const [type, setType] = useState("Personal Identity");
  const [mini, setMini] = useState(null);
  const [full, setFull] = useState(null);
  const [busy, setBusy] = useState(false);
  const [fullBusy, setFullBusy] = useState(false);
  const [fullGenerating, setFullGenerating] = useState(false);
  const [activeTab, setActiveTab] = useState("mini");
  const [history, setHistory] = useState([]);
  const [safety, setSafety] = useState(null);
  const [saving, setSaving] = useState(false);
  const [reportId, setReportId] = useState("");
  const [isPublic, setIsPublic] = useState(false);
  const [togglingPublic, setTogglingPublic] = useState(false);

  const loadHistory = async () => {
    try { const r = await api.get("/decode/history"); setHistory(r.data.reports || []); } catch (e) { /* ignore */ }
  };
  useEffect(() => { loadHistory(); }, []);

  // Prefill a word when navigated from a Universe word chain.
  useEffect(() => {
    const w = location.state?.word;
    if (w) {
      setText(w);
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line
  }, [location.state]);

  const runMini = async () => {
    if (!text.trim()) return;
    setBusy(true); setFull(null); setSafety(null); setFullGenerating(false); setActiveTab("mini");
    try {
      const r = await api.post("/decode/mini", { text, decode_type: type });
      if (r.data.safety) { setSafety(r.data.safety); setMini(null); return; }
      setMini(r.data);
      setReportId(r.data.id || "");
      setIsPublic(false);
      await Promise.all([loadHistory(), refresh()]);
      if (r.data.reward?.leveled_up) toast.success(`Ranked up to ${r.data.reward.rank}!`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Decode failed");
    } finally { setBusy(false); }
  };

  const pollFull = async (id, inputText) => {
    // The AI report generates in the background (can take ~a minute). Poll until ready.
    for (let i = 0; i < 60; i++) {
      await new Promise((res) => setTimeout(res, 2500));
      try {
        const rr = await api.get(`/decode/report/${id}`);
        if (rr.data.full_report) {
          setFull({ input_text: inputText, full_report: rr.data.full_report, rule_output: rr.data.rule_output_json, mini: rr.data.mini });
          setFullGenerating(false);
          await loadHistory();
          toast.success("Full report ready");
          return;
        }
      } catch (e) { /* keep polling */ }
    }
    setFullGenerating(false);
    toast.error("Report is taking longer than usual — find it in History shortly.");
  };

  const runFull = async () => {
    if (!text.trim()) return;
    if ((user?.credits ?? 0) <= 0) { toast.error("You need a full decode credit. Upgrade or buy one."); navigate("/pricing"); return; }
    setFullBusy(true); setFull(null); setSafety(null); setFullGenerating(true); setActiveTab("full");
    try {
      const r = await api.post("/decode/full", { text, decode_type: type });
      if (r.data.safety) { setSafety(r.data.safety); setFullGenerating(false); return; }
      setMini({ input_text: r.data.input_text, mini: r.data.mini, rule_output: r.data.rule_output });
      setReportId(r.data.id || "");
      setIsPublic(false);
      await refresh();
      if (r.data.full_report) {
        setFull({ input_text: r.data.input_text, full_report: r.data.full_report, rule_output: r.data.rule_output, mini: r.data.mini });
        setFullGenerating(false);
        await loadHistory();
        toast.success("Full report ready");
      } else {
        await pollFull(r.data.id, r.data.input_text);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Full decode failed");
      setFullGenerating(false);
    } finally { setFullBusy(false); }
  };

  const saveToVault = async () => {
    if (!mini) return;
    setSaving(true);
    try {
      const r = await api.post("/vault", { word: mini.input_text, decode_type: type, report_id: mini.id || "" });
      toast.success(r.data.already ? "Already in your Vault" : "Saved to Vault");
    } catch (e) { toast.error("Could not save"); } finally { setSaving(false); }
  };

  const togglePublic = async (checked) => {
    if (!reportId) { toast.error("Run a decode first"); return; }
    setTogglingPublic(true);
    try {
      const r = await api.post(`/decode/report/${reportId}/public`, { is_public: checked });
      setIsPublic(r.data.is_public);
      toast.success(r.data.is_public ? "Shared to public gallery" : "Removed from gallery");
    } catch (e) { toast.error("Could not update"); } finally { setTogglingPublic(false); }
  };

  const openReport = async (id) => {
    try {
      const r = await api.get(`/decode/report/${id}`);
      setMini({ input_text: r.data.input_text, mini: r.data.mini, rule_output: r.data.rule_output_json });
      setReportId(id);
      setIsPublic(!!r.data.is_public);
      if (r.data.full_report) {
        setFull({ input_text: r.data.input_text, full_report: r.data.full_report, rule_output: r.data.rule_output_json, mini: r.data.mini });
        setFullGenerating(false);
        setActiveTab("full");
      } else if (r.data.full_status === "generating") {
        setFull(null); setFullGenerating(true); setActiveTab("full");
        pollFull(id, r.data.input_text);
      } else {
        setFull(null); setFullGenerating(false); setActiveTab("mini");
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) { toast.error("Could not open report"); }
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-display font-semibold">Decode Mode</h1>
        <p className="text-[var(--bone-muted)] text-sm">The rule engine reads first. AI writes the full report only from the structure.</p>
      </div>

      <div className="litra-card p-5">
        <div className="flex gap-2">
          <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Enter a name, brand, word, or phrase…"
            className="bg-white/[0.04] border-white/[0.1]" data-testid="decode-word-input"
            onKeyDown={(e) => e.key === "Enter" && runMini()} />
          <Button onClick={runMini} disabled={busy} className="bg-[var(--primary)] hover:opacity-90" data-testid="decode-submit-button">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Mini decode"}
          </Button>
        </div>
        <div className="mt-3 flex flex-wrap gap-1.5" data-testid="decode-type-selector">
          {TYPES.map((t) => (
            <button key={t} onClick={() => setType(t)}
              className={`mono-chip rounded-full px-3 py-1 border transition-colors ${type === t ? "border-[rgba(20,90,67,0.6)] green-text bg-[rgba(20,90,67,0.12)]" : "border-white/[0.08] text-[var(--bone-faint)] hover:text-[var(--bone-muted)]"}`}
              data-testid={`decode-type-${t.replace(/\s+/g, "-").toLowerCase()}`}>
              {t}
            </button>
          ))}
        </div>
        <div className="mt-4 flex items-center gap-2 flex-wrap">
          <Button onClick={runFull} disabled={fullBusy} className="gold-edge gold-text bg-transparent hover:bg-[rgba(176,141,60,0.12)]" data-testid="decode-full-button">
            {fullBusy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
            {fullBusy ? "Generating report…" : "Full AI Report (1 credit)"}
          </Button>
          <span className="text-xs text-[var(--bone-faint)]">{user?.credits ?? 0} credits available</span>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          {mini && (
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="bg-white/[0.04]">
                <TabsTrigger value="mini" data-testid="decode-tab-mini">Mini</TabsTrigger>
                <TabsTrigger value="scores" data-testid="decode-tab-scores">All Scores</TabsTrigger>
                <TabsTrigger value="full" data-testid="decode-tab-full">Full Report</TabsTrigger>
                <TabsTrigger value="share" data-testid="decode-tab-share">Share</TabsTrigger>
              </TabsList>
              <TabsContent value="mini" className="mt-4"><DecodeMiniCard data={mini} decodeType={type} /></TabsContent>
              <TabsContent value="scores" className="mt-4">
                <div className="litra-card p-5"><div className="text-sm font-display font-semibold mb-4">13 Scoring Categories</div>
                  <ScoreBars scores={mini.rule_output?.scores} /></div>
              </TabsContent>
              <TabsContent value="full" className="mt-4">
                {full ? (
                  <div className="space-y-3">
                    <div className="flex justify-end">
                      <Button variant="outline" size="sm" className="glass gold-edge gold-text" onClick={() => exportReportPdf(full.full_report, full.input_text)} data-testid="decode-report-pdf-button">
                        <FileDown className="w-4 h-4 mr-2" /> Export PDF
                      </Button>
                    </div>
                    <DecodeFullReport report={full.full_report} inputText={full.input_text} />
                  </div>
                ) : fullGenerating ? (
                  <div className="litra-card p-8 text-center" data-testid="decode-full-generating">
                    <Loader2 className="w-6 h-6 mx-auto gold-text animate-spin" />
                    <p className="mt-3 text-[var(--bone)] text-sm font-display font-semibold">Writing your full Litrametry report…</p>
                    <p className="mt-1 text-[var(--bone-faint)] text-xs">The AI is composing all 21 sections from the rule structure. This can take up to a minute.</p>
                    <div className="mt-5 space-y-2 max-w-md mx-auto text-left">
                      {[0, 1, 2, 3, 4].map((i) => (
                        <div key={i} className="space-y-1.5">
                          <div className="h-2.5 rounded bg-white/[0.06] animate-pulse" style={{ width: `${70 - i * 6}%` }} />
                          <div className="h-2 rounded bg-white/[0.04] animate-pulse" style={{ width: `${92 - i * 4}%` }} />
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="litra-card p-8 text-center">
                    <Lock className="w-6 h-6 mx-auto gold-text" />
                    <p className="mt-2 text-[var(--bone-muted)] text-sm">Generate the AI-assisted 21-section report (1 credit).</p>
                    <Button onClick={runFull} disabled={fullBusy} className="mt-3 gold-edge gold-text bg-transparent" data-testid="decode-generate-full-inline">Generate full report</Button>
                  </div>
                )}
              </TabsContent>
              <TabsContent value="share" className="mt-4 space-y-3">
                <div className="litra-card p-4 flex items-center justify-between gap-3" data-testid="decode-public-row">
                  <div className="flex items-start gap-2">
                    <Globe className="w-4 h-4 green-text mt-0.5" />
                    <div>
                      <div className="text-sm">Share to Public Gallery</div>
                      <div className="text-xs text-[var(--bone-faint)]">Let others discover and like this wordbody.</div>
                    </div>
                  </div>
                  <Switch checked={isPublic} onCheckedChange={togglePublic} disabled={togglingPublic || !reportId} data-testid="decode-public-toggle" />
                </div>
                <ShareCardComposer
                  inputText={mini.input_text}
                  bindStatement={`${mini.rule_output?.combined?.litraroot?.technical_name} \u2192 ${mini.rule_output?.combined?.litraseal?.technical_name}`}
                  signals={(mini.mini?.top_letter_signals || []).map((s) => `${s.letter} · ${s.technical_name}`)}
                  archetype={mini.mini?.archetype}
                  reportId={reportId}
                  watermark={user?.plan === "free"}
                />
              </TabsContent>
            </Tabs>
          )}
          {!mini && <div className="litra-card p-10 text-center text-[var(--bone-faint)]">Enter a word above to begin decoding.</div>}
        </div>

        <div className="space-y-2">
          <div className="text-sm font-display font-semibold">History</div>
          <div className="space-y-2 max-h-[560px] overflow-auto scrollbar-thin pr-1" data-testid="decode-history">
            {history.length === 0 && <div className="text-xs text-[var(--bone-faint)]">No decodes yet.</div>}
            {history.map((h) => (
              <button key={h.id} onClick={() => openReport(h.id)} className="w-full text-left litra-card litra-card-hover p-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm">{h.input_text}</span>
                  {h.full_report ? <Badge className="gold-edge gold-text bg-transparent text-[10px]">Full</Badge> : null}
                </div>
                <div className="text-xs text-[var(--bone-faint)]">{h.word_class} · {h.decode_type}</div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
